import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Heading,
  Button,
  Notification,
  TextLink,
  Note,
  Paragraph,
  Tabs,
  TextInput,
  Select,
  IconButton,
  Badge,
  Spinner,
  Table,
} from "@contentful/f36-components";
import {
  ExternalLinkIcon,
  PlusCircleIcon,
  LanguageIcon,
  FolderOpenIcon,
  EnvironmentAliasIcon,
  CycleIcon,
  DeleteIcon,
  SearchIcon,
  ListBulletedIcon,
} from "@contentful/f36-icons";
import { PageAppSDK } from "@contentful/app-sdk";
import { ApiClient } from "../gpi-api/apiClient";
import logo from "../logo.png";
import DynamicTable from "./UI/DynamicTable";
import { BadgeStatus } from "./UI/BadgeStatus";
import { OpenNewRequestModal, OpenTranslationStatusReportModal } from "./ModalActions/OpenModals";
import { onImport } from "./ModalActions/OnImport";
import { onArchive } from "./ModalActions/OnArchive";
import { CleanFilesAction } from "./ModalActions/CleanFilesAction";
import { fetchJobs, deleteJob, Job, JobType } from "../api/backendClient";

interface PageProps {
  sdk: PageAppSDK;
}

interface ProjectTablesProps {
  Id: number;
  Name: string;
  Status: JSX.Element;
  CreatedBy: string;
  CreatedOn: string;
  Source: string;
  Target: string;
  Actions: JSX.Element;
}

// Filter type for jobs
type JobFilterType = 'all' | 'Import' | 'AI Translation' | 'Export';

const Page = (props: PageProps) => {
  // Gets the Contentful SDK
  const { sdk } = props;

  const [quotes, setQuotes] = useState<ProjectTablesProps[]>([]);
  const [projects, setProjects] = useState<ProjectTablesProps[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("dashboard");

  // Jobs tab state
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsSearch, setJobsSearch] = useState("");
  const [jobsFilter, setJobsFilter] = useState<JobFilterType>("all");

  //const spacesIdsAllowed = ['m9mcybwguwgq','2yuyw88gxocw']
  const spacesIdsAllowed = ['none']
  const currentYear = new Date().getFullYear();

  const headers = [
    { Name: "Name", Field: "Name" },
    { Name: "Status", Field: "Status" },
    { Name: "Created By", Field: "CreatedBy" },
    { Name: "Created On", Field: "CreatedOn" },
    { Name: "Source", Field: "Source" },
    { Name: "Target", Field: "Target" },
    { Name: "Actions", Field: "Actions", Width: "15%" },
  ];

  // Fetch jobs from backend
  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const result = await fetchJobs(sdk.ids.space);
      if (result.success && result.data) {
        setJobs(result.data.jobs);
      }
    } catch (error: any) {
      Notification.error("Failed to load jobs: " + error.message);
    } finally {
      setJobsLoading(false);
    }
  }, [sdk.ids.space]);

  // Handle job deletion
  const handleDeleteJob = async (jobId: string) => {
    const confirmed = await sdk.dialogs.openConfirm({
      title: "Delete Job",
      message: "Are you sure you want to delete this job?",
      intent: "negative",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteJob(jobId);
      Notification.success("Job deleted successfully");
      loadJobs(); // Refresh the list
    } catch (error: any) {
      Notification.error("Failed to delete job: " + error.message);
    }
  };

  // Get status message based on job type and status
  const getJobStatusMessage = (job: Job): string => {
    if (job.status === 'failed') {
      return job.error || 'Job failed';
    }
    if (job.status === 'processing') {
      return 'Processing...';
    }
    if (job.status === 'pending') {
      return 'Pending...';
    }
    // Completed
    if (job.type === 'AI Translation') {
      return 'Translation completed successfully';
    }
    if (job.type === 'Export') {
      return 'Quote submitted successfully';
    }
    if (job.type === 'Import') {
      return 'Import completed successfully';
    }
    return 'Completed';
  };

  // Get badge variant for job type
  const getJobTypeBadge = (type: JobType): JSX.Element => {
    switch (type) {
      case 'AI Translation':
        return <Badge variant="primary">AI TRANSLATION</Badge>;
      case 'Export':
        return <Badge variant="warning">EXPORT</Badge>;
      case 'Import':
        return <Badge variant="secondary">IMPORT</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  // Get badge for job status
  const getJobStatusBadge = (status: string): JSX.Element => {
    switch (status) {
      case 'completed':
        return <Badge variant="positive">COMPLETED</Badge>;
      case 'failed':
        return <Badge variant="negative">FAILED</Badge>;
      case 'processing':
        return <Badge variant="primary">PROCESSING</Badge>;
      case 'pending':
        return <Badge variant="secondary">PENDING</Badge>;
      default:
        return <Badge>{status.toUpperCase()}</Badge>;
    }
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter and search jobs
  const filteredJobs = useMemo(() => {
    let result = jobs;

    // Apply type filter
    if (jobsFilter !== 'all') {
      result = result.filter(job => job.type === jobsFilter);
    }

    // Apply search
    if (jobsSearch.trim()) {
      const search = jobsSearch.toLowerCase();
      result = result.filter(job =>
        job.id.toLowerCase().includes(search) ||
        job.title.toLowerCase().includes(search) ||
        job.createdBy.toLowerCase().includes(search) ||
        job.type.toLowerCase().includes(search) ||
        job.status.toLowerCase().includes(search) ||
        job.sourceLanguage.toLowerCase().includes(search) ||
        job.targetLanguages.some(lang => lang.toLowerCase().includes(search))
      );
    }

    return result;
  }, [jobs, jobsFilter, jobsSearch]);

  // Count jobs by type
  const jobCounts = useMemo(() => {
    return {
      all: jobs.length,
      import: jobs.filter(j => j.type === 'Import').length,
      aiTranslation: jobs.filter(j => j.type === 'AI Translation').length,
      export: jobs.filter(j => j.type === 'Export').length,
    };
  }, [jobs]);

  // Load jobs when switching to jobs tab
  useEffect(() => {
    if (activeTab === 'jobs') {
      loadJobs();
    }
  }, [activeTab, loadJobs]);

  useEffect(() => {
    const createStatus = (status: string) => {
      return <BadgeStatus status={status} />;
    };

    const createAction = (project: any) => {
      return (
        <>
          {project.portalLink && (
            <TextLink
              icon={<ExternalLinkIcon />}
              alignIcon="start"
              style={{ margin: "2px" }}
              href={project.portalLink}
              target="_blank"
              rel="noopener noreferrer"
              className="dashboard-link"
            >
              View Status Report
            </TextLink>
          )}
          {project.status === "ProjectCompleted" && (
            <TextLink
              alignIcon="start"
              variant="positive"
              style={{ margin: "2px" }}
              icon={<LanguageIcon />}
              onClick={() => onImport(project.id,project.name, sdk)}
              className="dashboard-link"
            >
              Import
            </TextLink>
          )}
          {project.status === "ProjectCompleted" && (
            <TextLink
              alignIcon="start"
              variant="negative"
              style={{ margin: "2px" }}
              icon={<FolderOpenIcon />}
              onClick={() => onArchive(project.id, sdk)}
              className="dashboard-link"
            >
              Archive
            </TextLink>
          )}
          {/* {project.portalLink && project.status === "ProjectCompleted" && (
            <TextLink
              icon={<PreviewIcon />}
              alignIcon="start"
              style={{ color: "black", margin: "2px" }}
              onClick={() => OpenReviewModal(project, sdk)}
              className="dashboard-link"
            >
              Review
            </TextLink>
          )} */}
        </>
      );
    };

    const reduceData = (resQuotes: any) => {
      const resp: ProjectTablesProps[] = [];
      resQuotes.forEach((element: any) => {
        let quote = {} as ProjectTablesProps;

        quote.Name = element.name;
        quote.Status = createStatus(element.status);
        quote.CreatedBy = element.createdBy as string;
        quote.CreatedOn = `${new Date(element.lastModifiedOn).toLocaleDateString()} ${new Date(
          element.lastModifiedOn,
        ).toLocaleTimeString()}`;
        quote.Source = element.sourceLanguage;
        quote.Target = element.targetLanguages.join(", ");
        quote.Actions = createAction(element);

        resp.push(quote);
      });

      return resp;
    };

    const getQuotes = () => {
      const parameters: any = sdk.parameters.installation;
      const client = new ApiClient(parameters.secretKey, parameters.authToken, parameters.exportFormat, "prod");

      return client.getQuotes().then(
        (result) => {
          const resQuotes = result.filter((res: any) => {
            return !res.status.includes("QuoteOpen") && !res.status.includes("Project");
          });
          const resProjects = result.filter((res: any) => {
            return !res.status.includes("QuoteOpen") && res.status.includes("Project");
          });

          const quotesReduced = reduceData(resQuotes);
          const projectsReduced = reduceData(resProjects);

          setQuotes(quotesReduced);
          setProjects(projectsReduced);

          setDataLoaded(true);
        },
        (error) => {
          Notification.error("An error ocurred while getting projects information. Details: " + error);
        },
      );
    };

    getQuotes();
  }, [sdk, sdk.parameters.installation]);

  return (
    <div style={{ width: "95%", marginLeft: "auto", marginRight: "auto", marginTop: "10px", backgroundColor: "white", padding: "20px", borderRadius: "10px" }} className="f36-margin-top--xl">
      <a href="https://www.globalizationpartners.com/" target="_blank" rel="noreferrer">
        <img src={logo} style={{ width: "400px", marginBottom: "15px" }} alt={"gpi logo"} />
      </a>
      <hr />

      <Tabs currentTab={activeTab} onTabChange={setActiveTab} style={{ marginTop: "10px" }}>
        <Tabs.List variant="horizontal-divider">
          <Tabs.Tab panelId="dashboard"><span style={{ fontSize: "16px" }}>Dashboard</span></Tabs.Tab>
          <Tabs.Tab panelId="jobs"><span style={{ fontSize: "16px" }}>Jobs</span></Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel id="dashboard" style={{ marginTop: "20px" }}>
          <div style={{ marginBottom: "40px" }}>
            <Button
              variant="positive"
              size="small"
              style={{ float: "right" }}
              endIcon={<PlusCircleIcon />}
              onClick={() => OpenNewRequestModal(sdk)}
            >
              New Request
            </Button>
            <Button
              variant="secondary"
              size="small"
              style={{ float: "right", marginRight: "10px" }}
              endIcon={<ListBulletedIcon />}
              onClick={() => OpenTranslationStatusReportModal(sdk)}
            >
              Translation Status Report
            </Button>
            {
              sdk.ids.space && spacesIdsAllowed.includes(sdk.ids.space) &&

              <Button
                variant="secondary"
                size="small"
                style={{ float: "right", marginRight: "10px" }}
                endIcon={<EnvironmentAliasIcon />}
                onClick={() => CleanFilesAction(sdk)}
              >
                Clean translations
              </Button>
            }
            <Heading style={{ fontSize: "20px" }}>Quotes</Heading>
            <DynamicTable data={quotes} headers={headers} style={{ marginTop: "15px" }} isLoading={!dataLoaded} />
            {dataLoaded && quotes.length === 0 && <Note style={{ marginTop: "15px" }}>There are no quotes to show.</Note>}
          </div>
          <div style={{ marginTop: "40px" }}>
            <Heading style={{ fontSize: "20px" }}>Projects</Heading>
            <DynamicTable data={projects} headers={headers} style={{ marginTop: "15px" }} isLoading={!dataLoaded} />

            {dataLoaded && projects.length === 0 && (
              <Note style={{ marginTop: "15px" }}>There are no translation projects to show.</Note>
            )}
          </div>
        </Tabs.Panel>

        <Tabs.Panel id="jobs" style={{ marginTop: "20px" }}>
          <div>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <Heading style={{ fontSize: "28px", margin: 0 }}>
                Jobs ({filteredJobs.length})
              </Heading>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Select
                  id="jobsFilter"
                  name="jobsFilter"
                  value={jobsFilter}
                  onChange={(e) => setJobsFilter(e.target.value as JobFilterType)}
                  style={{ minWidth: "200px" }}
                >
                  <Select.Option value="all">All Jobs ({jobCounts.all})</Select.Option>
                  <Select.Option value="Import">Import Jobs ({jobCounts.import})</Select.Option>
                  <Select.Option value="AI Translation">AI Translation ({jobCounts.aiTranslation})</Select.Option>
                  <Select.Option value="Export">Quote Submission ({jobCounts.export})</Select.Option>
                </Select>
                <IconButton
                  variant="primary"
                  aria-label="Refresh jobs"
                  icon={<CycleIcon />}
                  onClick={loadJobs}
                  isLoading={jobsLoading}
                />
              </div>
            </div>

            {/* Search */}
            <div style={{ marginBottom: "20px" }}>
              <TextInput
                id="jobsSearch"
                name="jobsSearch"
                placeholder="Search by job ID, user ID, quote ID, title, language, status, or type..."
                value={jobsSearch}
                onChange={(e) => setJobsSearch(e.target.value)}
                icon={<SearchIcon />}
              />
            </div>

            {/* Jobs Table */}
            {jobsLoading && jobs.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spinner size="large" />
                <Paragraph style={{ marginTop: "10px" }}>Loading jobs...</Paragraph>
              </div>
            ) : filteredJobs.length === 0 ? (
              <Note style={{ marginTop: "15px" }}>
                {jobs.length === 0
                  ? "There are no jobs to show."
                  : "No jobs match your search criteria."}
              </Note>
            ) : (
              <Table>
                <Table.Head>
                  <Table.Row>
                    <Table.Cell style={{ width: "30%", fontWeight: "bold", color: "#333" }}>Details</Table.Cell>
                    <Table.Cell style={{ width: "12%", fontWeight: "bold", color: "#333" }}>Type</Table.Cell>
                    <Table.Cell style={{ width: "12%", fontWeight: "bold", color: "#333" }}>Status</Table.Cell>
                    <Table.Cell style={{ width: "10%", fontWeight: "bold", color: "#333" }}>Progress</Table.Cell>
                    <Table.Cell style={{ width: "15%", fontWeight: "bold", color: "#333" }}>User ID</Table.Cell>
                    <Table.Cell style={{ width: "15%", fontWeight: "bold", color: "#333" }}>Created</Table.Cell>
                    <Table.Cell style={{ width: "6%" }}></Table.Cell>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {filteredJobs.map((job) => (
                    <Table.Row key={job.id}>
                      <Table.Cell>
                        <div>
                          <div style={{ fontWeight: 500 }}>{job.title}</div>
                          <div style={{ fontSize: "12px", color: job.status === 'failed' ? '#d32f2f' : '#22863a' }}>
                            {getJobStatusMessage(job)}
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell>{getJobTypeBadge(job.type)}</Table.Cell>
                      <Table.Cell>{getJobStatusBadge(job.status)}</Table.Cell>
                      <Table.Cell>{job.progress}%</Table.Cell>
                      <Table.Cell>
                        <span title={job.createdBy} style={{
                          display: "block",
                          maxWidth: "150px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}>
                          {job.createdBy}
                        </span>
                      </Table.Cell>
                      <Table.Cell>{formatDate(job.createdAt)}</Table.Cell>
                      <Table.Cell>
                        <IconButton
                          variant="negative"
                          aria-label="Delete job"
                          icon={<DeleteIcon variant="negative" />}
                          onClick={() => handleDeleteJob(job.id)}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </div>
        </Tabs.Panel>
      </Tabs>

      <div style={{ textAlign: "center", marginTop: "40px" }} className="f36-margin-top--xl">
        <Paragraph>
          &copy; {currentYear} Globalization Partners International
        </Paragraph>
      </div>
    </div>
  );
};

export default Page;
